///
/// EarthBrowser
///
/// Layers Panel
///
/// What's on the globe: the layer catalog as a dense list grouped by
/// Earth-system domain (layerRegistry LAYER_GROUP_ORDER). Groups collapse
/// to their header (persisted, userInterface.layersPanel) and show how
/// many of their layers are on; parents with sub-layers get a disclosure
/// that follows the switch (open while the parent is on) until the user
/// overrides it. The header's "All | N on" segment doubles as the active
/// count and a filter to just the layers on the globe.
///
/// The switch (and, on desktop, the row itself) toggles globe visibility;
/// the table button opens the layer's browser in the data dock. On phones
/// tapping a row opens the browser sheet, which replaces this one. The
/// filter field narrows the catalog by name or description; typing
/// something that isn't a layer hands off to the ⌘K palette
/// (controls/search/CommandPalette.tsx), which searches everything else.
/// Renders as a left-side panel on desktop and a bottom sheet on mobile.
///
/// Personal content (saved views, places, stories, recents) lives in the
/// Views menu at the top-right; app-level items (tutorial, version, dev
/// tools) in the account menu. This panel is only layers.
///
/// Created by Matt Giger on 9/6/2026
/// Copyright (c) 2026 Geodata Labs
///

import { Input } from "@/components/ui/input"
import { LoadRing } from "@/components/ui/load-ring"
import { useIsMobile } from "@/components/utils/use-mobile"
import { windowDatasetKey } from "@/components/windows/datasetWindowMeta"
import { cLoadPhaseOrder } from "@/earthbrowser/datasets/LoadState"
import { cLayersPanelEdgeInset, cLayersPanelWidth, cLegendTrayLiftVar } from "@/hooks/useBottomChromeOffset"
import { useLayerLoadStates, type DisplayedLoadState } from "@/hooks/useLayerLoadStates"
import { openCommandPalette, setLayersPanelOpen } from "@/redux/dialogSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { setLayerGroupCollapsed } from "@/redux/userInterfaceSlice"
import type { DatasetType } from "@/types/DatasetElement"
import { cn } from "@/utils/utils"
import { ChevronDown, Layers, Search, X } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { LayerRow } from "./LayerRow"
import { LAYER_GROUP_LABELS, LAYER_GROUP_ORDER, LAYERS, type LayerDefinition, type LayerGroup } from "./layerRegistry"
import { useLayerVisibility } from "./useLayerVisibility"

// Mobile detent sheet: dragging the handle snaps to these fractions of the
// viewport height; dragging below the dismiss fraction closes the sheet
const SHEET_DETENTS = [0.35, 0.65, 0.9]
const SHEET_DEFAULT_FRACTION = 0.65
const SHEET_DISMISS_FRACTION = 0.25

type CatalogView = "all" | "on"

interface Section {
	group: LayerGroup
	label: string
	/** Rows in display order; empty while the group is collapsed */
	layers: LayerDefinition[]
	/** Top-level layers of the group on the globe, filter or not */
	onCount: number
	collapsed: boolean
}

function layerMatches(layer: LayerDefinition, query: string): boolean {
	return layer.name.toLowerCase().includes(query) || layer.description.toLowerCase().includes(query)
}

export function LayersPanel() {
	const dispatch = useAppDispatch()
	const isMobile = useIsMobile()
	const isOpen = useAppSelector(state => state.dialog.layersPanelOpen)
	const { layerVisible, layerDisabled, activeCount, toggleLayer, openLayerTable } = useLayerVisibility()
	const [filter, setFilter] = useState("")
	const [selectedIndex, setSelectedIndex] = useState(0)
	// "All" is the catalog; "N on" is just the layers on the globe
	const [view, setView] = useState<CatalogView>("all")
	// Parent disclosure follows the parent's switch unless the user has
	// opened/closed it by hand; toggling the switch clears the override
	const [expandedOverrides, setExpandedOverrides] = useState<Record<string, boolean>>({})
	const collapsedGroups = useAppSelector(state => state.userInterface.layersPanel?.collapsedGroups ?? [])
	const inputRef = useRef<HTMLInputElement>(null)
	const listRef = useRef<HTMLDivElement>(null)

	// Mobile soft-keyboard avoidance
	const [keyboardInset, setKeyboardInset] = useState(0)
	const [sheetHeight, setSheetHeight] = useState<string | null>(null)

	// Mobile sheet drag-to-resize (detent sheet, like the bottom drawer)
	const sheetRef = useRef<HTMLDivElement>(null)
	const [dragHeight, setDragHeight] = useState<number | null>(null)
	const [isDraggingSheet, setIsDraggingSheet] = useState(false)
	const dragStartYRef = useRef(0)
	const dragStartHeightRef = useRef(0)
	const didDragRef = useRef(false)

	const trimmedFilter = filter.trim()
	const lowerFilter = trimmedFilter.toLowerCase()
	const filtering = trimmedFilter.length > 0

	const close = useCallback(() => {
		dispatch(setLayersPanelOpen(false))
		setFilter("")
		setSelectedIndex(0)
	}, [dispatch])

	// Escalate to the global palette, carrying the filter text along
	const searchEverything = useCallback(() => {
		dispatch(openCommandPalette(trimmedFilter))
		close()
	}, [dispatch, trimmedFilter, close])

	// Escape closes the panel. Capture phase so the bottom drawer's own
	// Escape handler doesn't also collapse.
	useEffect(() => {
		if (!isOpen) return

		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				e.stopPropagation()
				close()
			}
		}

		window.addEventListener("keydown", onKeyDown, true)
		return () => window.removeEventListener("keydown", onKeyDown, true)
	}, [isOpen, close])

	// Mobile soft-keyboard avoidance: when the keyboard opens, shift the
	// bottom sheet up and cap its height to the visible viewport.
	useEffect(() => {
		if (!isOpen || !isMobile || typeof window === "undefined" || !window.visualViewport) return

		const viewport = window.visualViewport
		const handleViewportChange = () => {
			const heightDiff = window.innerHeight - viewport.height
			const keyboardVisible = heightDiff > 150

			if (keyboardVisible) {
				const baseHeight = dragHeight ?? window.innerHeight * SHEET_DEFAULT_FRACTION
				setKeyboardInset(heightDiff)
				setSheetHeight(`${Math.round(Math.min(baseHeight, viewport.height - 24))}px`)
			} else {
				setKeyboardInset(0)
				setSheetHeight(null)
			}
		}

		viewport.addEventListener("resize", handleViewportChange)
		viewport.addEventListener("scroll", handleViewportChange)
		handleViewportChange()

		return () => {
			viewport.removeEventListener("resize", handleViewportChange)
			viewport.removeEventListener("scroll", handleViewportChange)
			setKeyboardInset(0)
			setSheetHeight(null)
		}
	}, [isOpen, isMobile, dragHeight])

	// Sheet handle drag: resize freely while dragging, then snap to the
	// nearest detent, or dismiss when dragged low enough
	const handleSheetDragStart = useCallback((e: React.PointerEvent) => {
		e.preventDefault()
		e.stopPropagation()

		const current = sheetRef.current?.getBoundingClientRect().height
			?? window.innerHeight * SHEET_DEFAULT_FRACTION
		dragStartYRef.current = e.clientY
		dragStartHeightRef.current = current
		didDragRef.current = false
		setIsDraggingSheet(true)

		if (e.pointerType === "touch") {
			(e.target as HTMLElement).setPointerCapture(e.pointerId)
		}
	}, [])

	const handleSheetDragMove = useCallback((e: PointerEvent) => {
		const delta = dragStartYRef.current - e.clientY
		if (Math.abs(delta) > 4) {
			didDragRef.current = true
		}
		const maxHeight = window.innerHeight * SHEET_DETENTS[SHEET_DETENTS.length - 1]
		setDragHeight(Math.max(64, Math.min(dragStartHeightRef.current + delta, maxHeight)))
	}, [])

	const handleSheetDragEnd = useCallback((e: PointerEvent) => {
		if (e.pointerType === "touch") {
			(e.target as HTMLElement)?.releasePointerCapture?.(e.pointerId)
		}
		setIsDraggingSheet(false)
		if (!didDragRef.current) return

		const current = sheetRef.current?.getBoundingClientRect().height
		if (current === undefined) return

		if (current < window.innerHeight * SHEET_DISMISS_FRACTION) {
			setDragHeight(null)
			close()
			return
		}

		const nearest = SHEET_DETENTS
			.map(fraction => fraction * window.innerHeight)
			.reduce((best, detent) =>
				Math.abs(detent - current) < Math.abs(best - current) ? detent : best
			)
		setDragHeight(Math.round(nearest))
	}, [close])

	useEffect(() => {
		if (!isDraggingSheet) return

		document.addEventListener("pointermove", handleSheetDragMove)
		document.addEventListener("pointerup", handleSheetDragEnd)
		return () => {
			document.removeEventListener("pointermove", handleSheetDragMove)
			document.removeEventListener("pointerup", handleSheetDragEnd)
		}
	}, [isDraggingSheet, handleSheetDragMove, handleSheetDragEnd])

	const handleOpenTable = useCallback((layer: LayerDefinition) => {
		// On phones the table sheet replaces this one; on desktop the data
		// dock sits on the right and both can stay open
		if (openLayerTable(layer) && isMobile) close()
	}, [openLayerTable, isMobile, close])

	// Which layer's table is showing in the dock, for the row chip's active state
	const activeDockWindow = useAppSelector(state => {
		const { isOpen: dockOpen, activeTabId, tabs } = state.dialog.bottomDrawer
		if (!dockOpen || !activeTabId || !tabs.some(t => t.windowId === activeTabId)) return null
		return state.dialog.datasetWindows[activeTabId] ?? null
	})
	const activeDockKey = windowDatasetKey(activeDockWindow?.windowType)

	const handleToggle = useCallback((type: DatasetType, checked: boolean) => {
		const parent = LAYERS.find(layer => layer.visibility === type && !layer.parentId)
		if (parent && expandedOverrides[parent.id] !== undefined) {
			setExpandedOverrides(prev => {
				const next = { ...prev }
				delete next[parent.id]
				return next
			})
		}
		toggleLayer(type, checked)
	}, [toggleLayer, expandedOverrides])

	const parentExpanded = useCallback((parent: LayerDefinition): boolean => {
		const override = expandedOverrides[parent.id]
		if (override !== undefined) return override
		return !!parent.visibility && layerVisible(parent.visibility)
	}, [expandedOverrides, layerVisible])

	const handleToggleExpanded = useCallback((parent: LayerDefinition) => {
		const next = !parentExpanded(parent)
		setExpandedOverrides(prev => ({ ...prev, [parent.id]: next }))
	}, [parentExpanded])

	const handleToggleGroup = useCallback((group: LayerGroup) => {
		dispatch(setLayerGroupCollapsed({ group, collapsed: !collapsedGroups.includes(group) }))
	}, [dispatch, collapsedGroups])

	// Grouped catalog. The filter overrides collapse and disclosure: a
	// parent that matches keeps all its sub-layers, a matching sub-layer
	// brings its parent along for context (the parent switch still gates
	// it). The "on" view keeps only layers on the globe
	const sections = useMemo((): Section[] => {
		const result: Section[] = []
		for (const group of LAYER_GROUP_ORDER) {
			const parents = LAYERS.filter(layer => layer.group === group && !layer.parentId)
			const onCount = parents.filter(layer => layer.visibility && layerVisible(layer.visibility)).length
			const collapsed = !filtering && collapsedGroups.includes(group)
			const rows = parents.flatMap(parent => {
				const children = LAYERS.filter(sub => sub.parentId === parent.id)
				const parentOn = !!parent.visibility && layerVisible(parent.visibility)
				if (view === "on" && !parentOn) return []

				let shownChildren: LayerDefinition[]
				if (filtering) {
					if (layerMatches(parent, lowerFilter)) {
						shownChildren = children
					} else {
						shownChildren = children.filter(sub => layerMatches(sub, lowerFilter))
						if (shownChildren.length === 0) return []
					}
				} else {
					shownChildren = parentExpanded(parent) ? children : []
				}
				if (view === "on") {
					shownChildren = shownChildren.filter(sub => sub.visibility && layerVisible(sub.visibility))
				}
				return [parent, ...shownChildren]
			})
			if (rows.length > 0) {
				result.push({ group, label: LAYER_GROUP_LABELS[group], layers: collapsed ? [] : rows, onCount, collapsed })
			}
		}
		return result
	}, [filtering, lowerFilter, view, collapsedGroups, layerVisible, parentExpanded])

	// Flattened list matching display order, for keyboard navigation
	const navItems = useMemo(() => sections.flatMap(section => section.layers), [sections])

	useEffect(() => {
		setSelectedIndex(0)
	}, [trimmedFilter, view])

	// Enter matches the row click: on desktop it toggles the layer (⌘Enter
	// or → opens the table); on phones it opens the table when there is
	// one. With no layer matches, Enter escalates to the palette
	const activateLayer = useCallback((layer: LayerDefinition, openTable: boolean) => {
		if (layerDisabled(layer)) return
		const wantsTable = isMobile || openTable
		if (wantsTable && layer.commandId) {
			handleOpenTable(layer)
		} else if (layer.visibility) {
			handleToggle(layer.visibility, !layerVisible(layer.visibility))
		}
	}, [layerDisabled, isMobile, handleOpenTable, handleToggle, layerVisible])

	const handlePanelKeyDown = useCallback((e: React.KeyboardEvent) => {
		switch (e.key) {
			case "ArrowDown":
				e.preventDefault()
				setSelectedIndex(prev => Math.min(prev + 1, navItems.length - 1))
				break
			case "ArrowUp":
				e.preventDefault()
				setSelectedIndex(prev => Math.max(prev - 1, 0))
				break
			case "Enter":
				e.preventDefault()
				if (navItems[selectedIndex]) {
					activateLayer(navItems[selectedIndex], e.metaKey || e.ctrlKey)
				} else if (filtering) {
					searchEverything()
				}
				break
			case "ArrowRight":
				if (!isMobile && navItems[selectedIndex]?.commandId) {
					e.preventDefault()
					activateLayer(navItems[selectedIndex], true)
				}
				break
		}
	}, [navItems, selectedIndex, activateLayer, filtering, searchEverything, isMobile])

	useEffect(() => {
		if (!listRef.current) return
		const selectedElement = listRef.current.querySelector(`[data-nav-index="${selectedIndex}"]`)
		selectedElement?.scrollIntoView({ block: "nearest" })
	}, [selectedIndex])

	if (!isOpen) return null

	// The active count is the label of the "on globe" view, so one control
	// both reports the number and filters to it
	const viewButtonClass = (active: boolean) => cn(
		"px-2 h-6 text-xs transition-colors",
		"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cyan-500/50",
		active ? "bg-white/10 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"
	)
	const header = (
		<div className="flex items-center gap-2 px-3 py-2 border-b border-white/10 flex-shrink-0">
			<Layers className="h-4 w-4 text-white/70" />
			<span className="text-sm font-semibold text-white">Layers</span>
			<div className="flex-1" />
			<div className="flex rounded-md border border-white/10 overflow-hidden" role="group" aria-label="Show layers">
				<button
					onClick={() => setView("all")}
					aria-pressed={view === "all"}
					className={viewButtonClass(view === "all")}
				>
					All
				</button>
				<button
					onClick={() => setView("on")}
					aria-pressed={view === "on"}
					className={cn(viewButtonClass(view === "on"), view === "on" && "text-cyan-300")}
					title="Just the layers on the globe"
				>
					{activeCount} on
				</button>
			</div>
			<button
				onClick={close}
				className="h-6 w-6 flex items-center justify-center rounded-full hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
				aria-label="Close layers panel"
			>
				<X className="h-4 w-4" />
			</button>
		</div>
	)

	const filterField = (
		<div className="px-2 pt-1.5 pb-1 flex-shrink-0">
			<div className="relative">
				<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500 pointer-events-none" />
				<Input
					ref={inputRef}
					value={filter}
					onChange={e => setFilter(e.target.value)}
					placeholder="Filter layers…"
					aria-label="Filter layers"
					className="h-8 pl-8 pr-2 text-sm bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-cyan-500/40"
				/>
			</div>
		</div>
	)

	const list = (
		<div ref={listRef} className="flex-1 min-h-0 overflow-y-auto px-1.5 pb-1.5">
			{(() => {
				let globalIndex = 0
				return sections.map(section => (
					<div key={section.group}>
						<button
							onClick={() => handleToggleGroup(section.group)}
							disabled={filtering}
							aria-expanded={!section.collapsed}
							className={cn(
								"flex w-full items-center gap-1.5 h-7 mt-1 px-1.5 rounded-md text-left",
								"text-[11px] font-semibold uppercase tracking-wider text-slate-400",
								"enabled:hover:bg-white/5 enabled:hover:text-slate-200 transition-colors",
								"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cyan-500/50"
							)}
						>
							<ChevronDown className={cn(
								"h-3 w-3 flex-shrink-0 transition-transform duration-300",
								section.collapsed && "-rotate-90",
								filtering && "opacity-0"
							)} />
							<span className="flex-1 truncate">{section.label}</span>
							{view === "all" && section.onCount > 0 && (
								<span className="font-normal normal-case tracking-normal text-cyan-300/80">
									{section.onCount} on
								</span>
							)}
						</button>
						{section.layers.map(layer => {
							const currentIndex = globalIndex++
							const hasChildren = !layer.parentId && LAYERS.some(sub => sub.parentId === layer.id)
							// Layers with their own controls (satellite sets) open
							// like parents; the strip goes where sub-layers would.
							// The "on" view already shows every sub-layer that's on,
							// so a disclosure there would open onto nothing
							const discloses = (hasChildren || !!layer.controls) && !filtering && view === "all"
							const expanded = discloses ? parentExpanded(layer) : undefined
							const Strip = layer.controls?.Strip
							const Summary = layer.controls?.Summary
							return (
								<div key={layer.id}>
									<LayerRow
										layer={layer}
										visible={layer.visibility ? layerVisible(layer.visibility) : false}
										disabled={layerDisabled(layer)}
										isMobile={isMobile}
										navIndex={currentIndex}
										isSelected={currentIndex === selectedIndex}
										rowAction={isMobile ? "open" : "toggle"}
										tableOpen={activeDockKey !== undefined && activeDockKey === layer.visibility}
										expanded={expanded}
										onToggleExpanded={handleToggleExpanded}
										summary={Summary ? <Summary /> : undefined}
										alignColumns
										onToggle={handleToggle}
										onOpenTable={handleOpenTable}
										onHover={() => setSelectedIndex(currentIndex)}
									/>
									{Strip && expanded && !layerDisabled(layer) && <Strip isMobile={isMobile} />}
								</div>
							)
						})}
					</div>
				))
			})()}
			{filtering && navItems.length === 0 && (
				<div className="px-3 py-6 text-center text-sm text-slate-500">
					No layers match “{trimmedFilter}”
				</div>
			)}
			{!filtering && view === "on" && sections.length === 0 && (
				<div className="px-3 py-6 text-center text-sm text-slate-500">
					<div>No layers on the globe.</div>
					<button
						onClick={() => setView("all")}
						className="mt-2 text-xs text-cyan-300 hover:text-cyan-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/50 rounded"
					>
						Show all layers
					</button>
				</div>
			)}
		</div>
	)

	// Handoff to the global palette. Reads as the answer to "it's not a
	// layer" while filtering, and as the way to search everything otherwise.
	const searchFooter = (
		<button
			onClick={searchEverything}
			className={cn(
				"flex items-center gap-2 px-3 py-2 border-t border-white/10 flex-shrink-0 text-left",
				"text-xs text-white/60 hover:text-white hover:bg-white/5 transition-colors",
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cyan-500/50"
			)}
		>
			<Search className="h-3.5 w-3.5 flex-shrink-0" />
			<span className="flex-1 min-w-0 truncate">
				{filtering ? <>Search everything for “{trimmedFilter}”</> : "Search places, satellites and actions"}
			</span>
			{!isMobile && (
				<kbd className="px-1.5 py-0.5 bg-white/10 rounded border border-white/20 font-mono text-[10px] text-white/50">
					⌘K
				</kbd>
			)}
		</button>
	)

	if (isMobile) {
		return (
			<>
				{/* Scrim */}
				<div
					className="fixed inset-0 z-[8000] bg-black/50"
					onClick={close}
					aria-hidden="true"
				/>
				{/* Bottom sheet */}
				<div
					ref={sheetRef}
					className={cn(
						"fixed inset-x-0 bottom-0 z-[8001] flex flex-col rounded-t-2xl bg-slate-950/90 backdrop-blur-xl border-t border-white/20",
						!sheetHeight && dragHeight === null && "h-[65vh]"
					)}
					style={{
						paddingBottom: keyboardInset > 0 ? undefined : "env(safe-area-inset-bottom, 0px)",
						height: sheetHeight ?? (dragHeight !== null ? `${Math.round(dragHeight)}px` : undefined),
						transform: keyboardInset > 0 ? `translateY(-${keyboardInset}px)` : undefined,
					}}
					onKeyDown={handlePanelKeyDown}
				>
					<div
						className="flex justify-center pt-2 pb-2 flex-shrink-0 cursor-ns-resize select-none touch-none"
						onPointerDown={handleSheetDragStart}
						role="separator"
						aria-label="Drag to resize layers panel"
					>
						<div className={cn(
							"w-9 h-1 rounded-full transition-colors",
							isDraggingSheet ? "bg-white/50" : "bg-white/20"
						)} />
					</div>
					{header}
					{filterField}
					{list}
					{searchFooter}
				</div>
			</>
		)
	}

	return (
		// Above the dataset drawer (z-[950]): the panel takes precedence when
		// both overlap. Geometry comes from the shared chrome constants so
		// the time bar and cutout HUDs (useChromeInsets().left) clear it.
		// The legend tray stays where it is: the panel ends above it, using
		// the height the tray publishes
		<div
			className="fixed z-[960] flex flex-col rounded-xl bg-black/50 backdrop-blur-xl border border-white/20 shadow-lg overflow-hidden transition-[bottom] duration-300"
			style={{
				top: "calc(1rem + var(--safe-area-inset-top))",
				left: cLayersPanelEdgeInset,
				width: cLayersPanelWidth,
				bottom: `calc(24px + var(${cLegendTrayLiftVar}, 0px))`,
			}}
			onKeyDown={handlePanelKeyDown}
		>
			{header}
			{filterField}
			{list}
			{searchFooter}
		</div>
	)
}

/** Launcher pill for the top-left row; hidden while the panel is open */
export function LayersButton() {
	const dispatch = useAppDispatch()
	const { activeCount } = useLayerVisibility()

	// Glance tier of the loading indicators: a ring on the icon while any
	// layer on the globe is still fetching or processing. Only visible
	// layers count (hidden ones syncing don't change the picture); the
	// earliest phase wins so an error dot outranks a spinning ring
	const loadStates = useLayerLoadStates()
	let aggregate: DisplayedLoadState | null = null
	for (const state of loadStates) {
		if (!state.visible) continue
		if (!aggregate || cLoadPhaseOrder.indexOf(state.phase) < cLoadPhaseOrder.indexOf(aggregate.phase)) {
			aggregate = state
		}
	}
	// The glance ring is always indeterminate: progress of one layer
	// means nothing for the set
	const glance = aggregate ? { ...aggregate, progress: undefined } : null
	const loadingCount = loadStates.filter(s => s.visible && !s.done).length

	return (
		<button
			onClick={() => dispatch(setLayersPanelOpen(true))}
			className={cn(
				// h-9: shared chrome control height (top-right cluster, camera stack, time bar)
				"flex items-center gap-2 h-9 rounded-xl px-3",
				"bg-black/40 backdrop-blur-xl border border-white/20 shadow-2xl",
				"hover:bg-black/50 hover:border-white/30 active:scale-95",
				"transition-all duration-300 touch-manipulation",
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
			)}
			aria-label={loadingCount > 0
				? `Open layers panel (${activeCount} layers on globe, ${loadingCount} loading)`
				: `Open layers panel (${activeCount} layers on globe)`}
			title={loadingCount > 0 ? `${loadingCount} ${loadingCount === 1 ? "layer is" : "layers are"} still loading data` : undefined}
		>
			<span className="relative flex items-center justify-center w-4 h-4">
				<Layers className="w-4 h-4 text-white/70" />
				<LoadRing state={glance} strokeWidth={2.5} />
			</span>
			{/* Phones: icon + count only, so the top row also fits Search,
			    the bookmark/Share pair and the account menu */}
			<span className="hidden sm:inline text-sm text-white/80">Layers</span>
			<span className="text-xs px-1.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
				{activeCount}
			</span>
		</button>
	)
}
